// Таблицата на „Продадени рецепти": ниски редове на един ред — # ·
// Наименование · Съставки · Продадено (бр) · Последна продажба · Ср. цена ·
// Нето · Бруто · „›", ред „Общо за периода"; карти на телефон. Заглавията
// сортират; Продадено и Бруто имат филтър от–до. Шевронът „›" казва, че редът
// се отваря: ↑/↓ по редовете, Enter/клик отваря панела на рецептата
// (`../sold/SoldRecipePanel`). „Продадени продукти" (теоретично срещу реално)
// е в `../sold/SoldProductsTable`.
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import type { SoldSellable } from "../../../../api/inventory";
import { fmtDateTime } from "../../components/dateTime";
import { formatMoney } from "../../components/numbers";
import { sectionList, type ColumnSections } from "../../components/filters/useColumnFilters";
import { EmptyDash, OpenChevron, ReportHead } from "./ReportListBits";
import {
  REPORT_HEAD_CLASS,
  REPORT_HEADER_ROW_CLASS,
  REPORT_OPENABLE_ROW_CLASS,
  REPORT_TOTAL_ROW_CLASS,
  moveRowFocus,
  openableRowProps,
} from "./reportListModel";
import { formatCount, type SoldFilterKey, type SoldSort, type SoldSortKey } from "./soldReportModel";

// Литерален клас — Tailwind сканира само пълни низове
const GRID_CLASS =
  "grid grid-cols-[40px_minmax(180px,1fr)_96px_140px_160px_120px_120px_120px_12px] items-center gap-2.5 px-[18px]";

type Props<T extends SoldSellable> = {
  rows: T[];
  isCards: boolean;
  sort: SoldSort | null;
  onSort: (key: SoldSortKey) => void;
  filters: ColumnSections<SoldFilterKey>;
  onOpen: (row: T) => void;
  totals: { net: number; gross: number };
  footer?: ReactNode;
};

export function SoldReportTable<T extends SoldSellable>({
  rows,
  isCards,
  sort,
  onSort,
  filters,
  onOpen,
  totals,
  footer,
}: Props<T>) {
  const { t } = useTranslation();

  // Продадени бройки (без мерна единица)
  const quantity = (row: T) => (
    <span className="whitespace-nowrap font-sheet font-bold tabular-nums text-ink">{formatCount(row.quantity)}</span>
  );

  if (isCards) {
    return (
      <div onKeyDown={moveRowFocus}>
        {rows.map((row) => (
          <div
            key={row.sellableId}
            data-sellable-id={row.sellableId}
            {...openableRowProps(() => onOpen(row))}
            className="flex cursor-pointer items-center gap-2.5 border-t border-shade-1 px-3.5 py-3 outline-none focus-visible:bg-fill"
          >
            <div className="flex min-w-0 flex-1 flex-col gap-[5px]">
              <div className="flex items-baseline gap-2">
                <span className="min-w-0 flex-1 truncate font-semibold text-ink">{row.name}</span>
                {quantity(row)}
              </div>
              <div className="flex items-baseline justify-between gap-2.5 text-[12.5px] text-body">
                <span className="min-w-0 truncate">
                  {t("inventoryPage.soldAvgLine", { price: formatMoney(row.avgPrice) })}
                  {` · ${t("inventoryPage.soldIngredientsLine", { count: row.ingredientsCount })}`}
                </span>
                <span className="font-sheet text-sm font-semibold text-ink">{formatMoney(row.totalSum)}</span>
              </div>
              {row.lastSoldAt && (
                <span className="text-[12.5px] text-body">
                  {t("inventoryPage.soldLastSoldLine", { date: fmtDateTime(row.lastSoldAt) })}
                </span>
              )}
            </div>
            <OpenChevron />
          </div>
        ))}
        {footer}
        <div className="flex items-baseline justify-between gap-2.5 border-t border-shade-2 bg-fill px-3.5 py-3">
          <span className={REPORT_HEAD_CLASS}>{t("inventoryPage.periodTotal")}</span>
          <span className="font-sheet font-bold text-brand-deep-ink">{formatMoney(totals.gross)}</span>
        </div>
      </div>
    );
  }

  const head = (
    label: string,
    options: { sortKey?: SoldSortKey; align?: "right"; filters?: ReturnType<typeof sectionList> } = {},
  ) => <ReportHead label={label} sort={sort} onSort={onSort} {...options} />;

  return (
    <div className="overflow-x-auto">
      <div className="min-w-[1104px]" onKeyDown={moveRowFocus}>
        <div className={`${GRID_CLASS} ${REPORT_HEADER_ROW_CLASS}`}>
          {head("#")}
          {head(t("inventoryPage.soldColName"), { sortKey: "name" })}
          {head(t("inventoryPage.soldColIngredients"), { sortKey: "ingredients", align: "right" })}
          {head(t("inventoryPage.soldColSold"), { sortKey: "qty", align: "right", filters: sectionList(filters.qty) })}
          {head(t("inventoryPage.soldColLastSold"), { sortKey: "lastSold", align: "right" })}
          {head(t("inventoryPage.soldColAvgPrice"), { sortKey: "avgPrice", align: "right" })}
          {head(t("inventoryPage.colNet"), { sortKey: "net", align: "right" })}
          {head(t("inventoryPage.colGross"), { sortKey: "gross", align: "right", filters: sectionList(filters.gross) })}
          <span />
        </div>
        {rows.map((row, index) => (
          <div
            key={row.sellableId}
            data-sellable-id={row.sellableId}
            {...openableRowProps(() => onOpen(row))}
            className={`${GRID_CLASS} ${REPORT_OPENABLE_ROW_CLASS}`}
          >
            <span className="font-sheet text-faint">{index + 1}</span>
            <span className="truncate font-semibold text-ink">{row.name}</span>
            <span className="text-right font-sheet tabular-nums text-ink-2">{row.ingredientsCount}</span>
            <span className="text-right">{quantity(row)}</span>
            <span className="whitespace-nowrap text-right font-sheet tabular-nums text-ink-2">
              {row.lastSoldAt ? fmtDateTime(row.lastSoldAt) : <EmptyDash />}
            </span>
            <span className="text-right font-sheet tabular-nums text-ink-2">{formatMoney(row.avgPrice)}</span>
            <span className="text-right font-sheet tabular-nums text-ink-2">{formatMoney(row.sumNoVat)}</span>
            <span className="text-right font-sheet font-bold tabular-nums text-ink">{formatMoney(row.totalSum)}</span>
            <OpenChevron />
          </div>
        ))}
        {footer}
        {rows.length > 0 && (
          <div className={`${GRID_CLASS} ${REPORT_TOTAL_ROW_CLASS}`}>
            <span className={`${REPORT_HEAD_CLASS} col-span-6`}>{t("inventoryPage.periodTotal")}</span>
            <span className="text-right font-sheet font-semibold text-ink">{formatMoney(totals.net)}</span>
            <span className="text-right font-sheet font-bold text-brand-deep-ink">{formatMoney(totals.gross)}</span>
          </div>
        )}
      </div>
    </div>
  );
}

export default SoldReportTable;
